// src/components/Footer.js
import React, { useEffect, useRef } from 'react';

const Footer = () => {
  const canvasRef = useRef(null);
  const footerRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const footer = footerRef.current;
    if (!canvas || !footer) return;
    const ctx = canvas.getContext('2d');

    // Matrix effect parameters
    const binary = "01";
    const fontSize = 14;
    let columns = Math.floor(canvas.parentElement.offsetWidth / fontSize);
    let drops = Array(columns).fill(1);

    // Set canvas dimensions to match its parent's dimensions.
    const setCanvasSize = () => {
      canvas.width = canvas.parentElement.offsetWidth;
      canvas.height = canvas.parentElement.offsetHeight;
      columns = Math.floor(canvas.width / fontSize);
      drops = Array(columns).fill(1);
    };
    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    // Store the current mouse position (or null if not hovering)
    let mousePos = null;
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mousePos = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };
    const handleMouseLeave = () => {
      mousePos = null;
    };
    footer.addEventListener('mousemove', handleMouseMove);
    footer.addEventListener('mouseleave', handleMouseLeave);

    // Particle explosion effect variables
    let particles = [];
    const gravity = 0.3; // Controls how fast particles fall

    // Click event to trigger the particle explosion.
    const handleClick = (e) => {
      // Do not trigger the effect if clicking on a link or button.
      if (e.target.closest('a, button')) {
        return;
      }
      const rect = canvas.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;
      
      // Create particles (adjust the number for a bigger/smaller burst)
      const numParticles = 20;
      for (let i = 0; i < numParticles; i++) {
        // Choose a random direction for each particle.
        const angle = Math.random() * Math.PI * 2;
        // Speed between 2 and 5, with a slight upward bias.
        const speed = Math.random() * 3 + 2;
        const vx = Math.cos(angle) * speed;
        const vy = Math.sin(angle) * speed - 5;
        const text = binary[Math.floor(Math.random() * binary.length)];
        particles.push({
          x: clickX,
          y: clickY,
          vx,
          vy,
          text,
          alpha: 1, // Start fully opaque
        });
      }
    };
    footer.addEventListener('click', handleClick);

    // Draw function runs every ~33ms.
    const draw = () => {
      // Create the fading trail effect for the matrix.
      ctx.fillStyle = "rgba(0, 0, 0, 0.05)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw the Matrix rain digits.
      ctx.fillStyle = "rgba(0, 255, 70, 0.5)";
      ctx.font = `${fontSize}px monospace`;
      for (let i = 0; i < columns; i++) {
        const text = binary[Math.floor(Math.random() * binary.length)];
        const x = i * fontSize;
        const y = drops[i] * fontSize;
        ctx.fillText(text, x, y);
        if (y > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }

      // Optional: Matrix multiplication effect near the mouse.
      if (mousePos) {
        ctx.save();
        ctx.globalCompositeOperation = 'multiply';
        const radius = 50;
        const gradient = ctx.createRadialGradient(
          mousePos.x,
          mousePos.y,
          0,
          mousePos.x,
          mousePos.y,
          radius
        );
        gradient.addColorStop(0, "rgba(0,255,70,1)");
        gradient.addColorStop(1, "rgba(0,255,70,0)");
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(mousePos.x, mousePos.y, radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      // Update and draw explosion particles.
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        // Update velocity with gravity.
        p.vy += gravity;
        // Update position.
        p.x += p.vx;
        p.y += p.vy;
        // Fade out slowly.
        p.alpha -= 0.02;
        if (p.alpha <= 0) {
          particles.splice(i, 1);
          continue;
        }
        ctx.fillStyle = `rgba(0,255,70,${p.alpha})`;
        ctx.fillText(p.text, p.x, p.y);
      }
    };

    const intervalId = setInterval(draw, 33);

    // Cleanup event listeners on unmount.
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('resize', setCanvasSize);
      footer.removeEventListener('mousemove', handleMouseMove);
      footer.removeEventListener('mouseleave', handleMouseLeave);
      footer.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <footer
      id="contact"
      ref={footerRef}
      className="relative my-16 mb-0 rounded-lg overflow-hidden"
    >
      {/* Matrix Data Stream Background */}
      <div className="absolute inset-0">
        <canvas ref={canvasRef} className="w-full h-full block" />
      </div>
      {/* Footer Content */}
      <div className="relative z-10 bg-[#212121] bg-opacity-90 border border-[#4d4d4d] rounded-lg p-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Schedule Call Section */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4">
              Let's Connect!
            </h3>
            <p className="text-gray-300 mb-6">
              Like what you see? Reach out via email to collaborate or just say hello.
            </p>
            {/* Greyed-out Schedule Call button (non-interactive) */}
            <span
              className="inline-block px-6 py-3 bg-gray-500 text-gray-300 rounded-md transition duration-200 cursor-not-allowed"
            >
              Schedule Call
            </span>
          </div>
          {/* Site Navigation */}
          <nav>
            <h4 className="text-xl font-bold text-white mb-4">Site Navigation</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="/"
                  className="text-gray-300 hover:text-white transition duration-200"
                >
                  Home
                </a>
              </li>
              <li>
                <a
                  href="/about"
                  className="text-gray-300 hover:text-white transition duration-200"
                >
                  About
                </a>
              </li>
              <li>
                <a
                  href="#work"
                  className="text-gray-300 hover:text-white transition duration-200"
                >
                  Projects
                </a>
              </li>
              <li>
                <a
                  href="/blog"
                  className="text-gray-300 hover:text-white transition duration-200"
                >
                  Blog
                </a>
              </li>
              <li>
                <a
                  href="/contact"
                  className="text-gray-300 hover:text-white transition duration-200"
                >
                  Contact
                </a>
              </li>
            </ul>
          </nav>
        </div>
        <hr className="border-gray-600 my-8" />
        <div className="text-center text-gray-400">
          <p>&copy; {new Date().getFullYear()} Koby Pierce. All rights reserved.</p>
          <div className="flex justify-center space-x-6 mt-4">
            <a
              href="https://x.com/koby_pierce"
              className="hover:text-white transition duration-200"
            >
              𝕏
            </a>
            <a
              href="https://www.linkedin.com/in/kobypierce/"
              className="hover:text-white transition duration-200"
            >
              LinkedIn
            </a>
            <a
              href="https://github.com/KobyPierce"
              className="hover:text-white transition duration-200"
            >
              GitHub
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;